import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import HandleView from './HandleView';
import logging from '../logging';
import pc from '../ParametricCache';
import { Box3, BufferGeometry, Group, Mesh, Object3D } from 'three';
import { Door, Frame, Panel, Section, Wall } from '../domain';
import { OrderedEffects, zip } from '../utils';
import { blank } from '../MaterialFactory';
import { elevation } from '../measurement';
import { getGeometrySize } from '../geometry';
import { isExtrusionPull } from '../controllers/handleControllers';
import { setHandleDirection, setHandleHeight } from '../controllers/doorControllers';
import { clamp } from '../math/clamp';

const log = logging('view');

//log.level = log.TRACE;


type XYZ = {x: number, y: number, z: number};
const set = (vec: XYZ, x: number, y: number, z: number) => {
    vec.x = x;
    vec.y = y;
    vec.z = z;
}

type Side = "top" | "bottom" | "left" | "right" | null;
type SingleDoorProperties = {
    frame: Frame,
    panel: Panel,
    width: number,
    height: number,
    hinge: Side,
    elevation?: number,
};

const opposite = (side: Side): Side => {
    switch(side) {
        case "top":
            return "bottom";
        case "bottom":
            return "top";
        case "left":
            return "right";
        case "right":
            return "left";
        case null:
            return null;
    }
}

const box: Box3 = new Box3();

/**
 * The SingleDoor represents an individual door panel with a hinge on one side.
 * This is used directly by the DoorView for left, right, top, bottom, and
 * double doors. The FoldingDoor also uses the SingleDoor to create two door
 * panels attached to eachother by a hinge between them.
 */
class SingleDoor extends Object3D {

    frame: Mesh;
    panel: Mesh;
    handle: HandleView;
    pivot: Group;

    updates = new OrderedEffects();

    constructor() {
        super();
        log.debug('SingleDoor constructor', this.uuid);
        this.frame = new Mesh(new BufferGeometry(), blank);
        this.frame.name = 'Door Frame';
        this.frame.castShadow = this.frame.receiveShadow = true;

        this.panel = new Mesh(this.frame.geometry, blank);
        this.panel.name = 'Door Panel';
        this.panel.castShadow = this.panel.receiveShadow = true;

        this.pivot = new Group();
        this.pivot.name = 'Door Hinge Pivot';

        this.frame.userData.isSelectable = true;
        this.panel.userData.isSelectable = true;
        this.frame.userData.isDoorMesh = true;
        this.panel.userData.isDoorMesh = true;

        // We only add the frame in updateGeometry if we have geometry for the
        // frame. Otherwise, having an empty BufferGeometry() as a child can
        // cause problems.
        this.pivot.add(this.panel);
        this.add(this.pivot);
    }

    updatePivot(door: SingleDoorProperties) {
        log.debug('SingleDoor.updatePivot', this.uuid, door.hinge);
        this.pivot.position.set(0, 0, 0);
        this.frame.position.set(0, 0, 0);
        this.panel.position.set(0, 0, 0);
        switch(door.hinge) {
            case 'right':
                this.pivot.position.x = door.width;
                this.frame.position.x = -door.width;
                this.panel.position.x = -door.width;
                break;
            case 'top':
                this.pivot.position.y = door.height;
                this.frame.position.y = -door.height;
                this.panel.position.y = -door.height;
                break;
        }
    }

    async updateGeometry(door: SingleDoorProperties) {
        log.debug('SingleDoor.updateGeometry', this.uuid, door.hinge);

        this.updates.queue(
            async () => pc.make('FrameAndPanel',
                door.frame?.style,
                door.panel.style,
                door.width,
                door.height,
                door.panel.grain
                ) as any as BufferGeometry[],

            ([frameGeo, panelGeo]) => {
                log.debug('SingleDoor.updateGeometry async', this.uuid, door.hinge);
                if(frameGeo == null) {
                    this.frame.removeFromParent();
                } else {
                    this.pivot.add(this.frame);
                    this.frame.geometry = frameGeo;
                }
                this.panel.geometry = panelGeo;

                if(this.handle == null) {
                    Application.do('pipeline-update');
                    return;
                }
                // If this is a full-width handle, we position it so it's fully
                // the length of one side of the door.
                if(isExtrusionPull(this.handle.model))
                {
                    let side = this.handle.model.side;
                    if(side == null) {
                        side = opposite(door.hinge);
                    }
                    switch(side) {
                        case "left":
                            set(this.handle.model.position, 0, door.height, 0);
                            set(this.handle.model.scale, door.height, 1, 1);
                            set(this.handle.model.rotation, 0, 0, 1.5*Math.PI);
                            break;
                        case "right":
                            set(this.handle.model.position, door.width, 0, 0);
                            set(this.handle.model.scale, door.height, 1, 1);
                            set(this.handle.model.rotation, 0, 0, 0.5*Math.PI);
                            break;
                        case "top":
                            set(this.handle.model.position, door.width, door.height, 0);
                            set(this.handle.model.scale, door.width, 1, 1);
                            set(this.handle.model.rotation, 0, 0, Math.PI);
                            break;
                        case "bottom":
                            set(this.handle.model.position, 0, 0, 0);
                            set(this.handle.model.scale, door.width, 1, 1);
                            set(this.handle.model.rotation, 0, 0, 0);
                            break;
                        default:
                            log.error(`Unsupported full-width obj side: ${side}`);
                    }
                } else {
                    switch(door.hinge) {
                        case 'left':
                            this.handle.model.position.x = door.width - 0.75;
                            if(!this.handle.isNonRotating)
                                setHandleDirection(this.handle.model, this.handle.handle, 'v');
                            break;
                        case 'right':
                            this.handle.model.position.x = 0.75;
                            if(!this.handle.isNonRotating)
                                setHandleDirection(this.handle.model, this.handle.handle, 'v', 'r');
                            break;
                        case 'top':
                            this.handle.model.position.x = door.width / 2;
                            this.handle.model.position.y = 0.75;
                            break;
                        case 'bottom':
                            this.handle.model.position.x = door.width / 2;
                            this.handle.model.position.y = door.height - 0.75;
                            this.handle.rotation.z = Math.PI;
                            break;
                    }
                    if(this.handle.model.assetId) {
                        this.handle.model.position.z = 0.75;
                    }
                }

                Application.do('pipeline-update');
            }
        );
    }
}

/**
 * Creates two door panels with a hinge between them. We only support left and
 * right hinges. 
 */
class FoldingDoor extends Object3D {
    
    /** The panel attached to the cabient */
    first: SingleDoor;

    /**
     * The panel attached to the first door. This swings in the opposite
     * rotation compared to the first door.
     */
    second: SingleDoor;

    constructor() {
        super();

        this.first = new SingleDoor();
        this.first.name = 'First';
        this.add(this.first);
        this.second = new SingleDoor();
        this.second.name = 'Second';
        this.first.panel.add(this.second);
    }

    updatePivot(door: SingleDoorProperties) {
        log.debug('FoldingDoor.updatePivot');
        const properties = {...door, width: door.width/2};
        this.first.updatePivot(properties);
        this.second.updatePivot(properties);
    }

    updateGeometry(door: SingleDoorProperties) {
        log.debug('FoldingDoor.updateGeometry');
        const properties = {...door, width: door.width/2};
        this.first.updateGeometry(properties);
        this.second.updateGeometry(properties);
    }

}

export default class DoorView extends BaseView {


    declare model: Door;

    // Only one of these should be defined at any time
    single?: SingleDoor;
    double?: { left: SingleDoor, right: SingleDoor };
    singleFold? : FoldingDoor;
    doubleFold?: { left: FoldingDoor, right: FoldingDoor };

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    make() {
        this.name = 'Door';
        this.userData.isParametric = true;

        if(this.model.hinge == 'double') {
            log.debug('DoorView.make double door', this.uuid);
            this.double = {
                left: new SingleDoor(),
                right: new SingleDoor(),
            };
            this.add(this.double.left);
            this.add(this.double.right);
            this.highlights.add(this.double.left.frame);
            this.highlights.add(this.double.left.panel);
            this.highlights.add(this.double.right.frame);
            this.highlights.add(this.double.right.panel);

            // This supports the case where the hinge has changed to double
            // from a single hinged option.
            if(this.model.handles.length == 1) {
                const left = this.model.handles[0];
                const right = left.clone(true);
                this.model.handles = [left, right];

            }
            if (this.model.handles.length == 2){
                for(let [door, handle] of
                    zip([this.double.left, this.double.right], this.model.handles))
                {
                    const view = new HandleView(handle, this.app);
                    door.handle = view;
                    door.panel.add(view);
                }
            }
        }
        else if(this.model.hinge == 'bi-fold-double') {
            this.doubleFold = {
                left: new FoldingDoor(),
                right: new FoldingDoor(),
            }
            this.doubleFold.left.name = 'DoorView.doubleFold.left';
            this.add(this.doubleFold.left);
            this.doubleFold.right.name = 'DoorView.doubleFold.right';
            this.add(this.doubleFold.right);
            this.highlights.add(this.doubleFold.left.first.frame);
            this.highlights.add(this.doubleFold.left.first.panel);
            this.highlights.add(this.doubleFold.left.second.frame);
            this.highlights.add(this.doubleFold.left.second.panel);
            this.highlights.add(this.doubleFold.right.first.frame);
            this.highlights.add(this.doubleFold.right.first.panel);
            this.highlights.add(this.doubleFold.right.second.frame);
            this.highlights.add(this.doubleFold.right.second.panel);

            // If we have zero handles, we won't add any. Adding the extra
            // hinge supports the case where the hinge has been changed to
            // bi-fold-double from a single hinged option.
            if(this.model.handles.length == 1) {
                const
                    left = this.model.handles[0],
                    right = left.clone(true);
                this.model.handles = [left, right];
            }
            if(this.model.handles.length == 2) {
                const viewLeft = new HandleView(this.model.handles[0], this.app);
                this.doubleFold.left.second.handle = viewLeft;
                this.doubleFold.left.second.panel.add(viewLeft);

                const viewRight = new HandleView(this.model.handles[1], this.app);
                this.doubleFold.right.second.handle = viewRight;
                this.doubleFold.right.second.panel.add(viewRight);
            }
        }
        else if(this.model.hinge.startsWith('bi-fold')) {
            this.singleFold = new FoldingDoor();
            this.singleFold.name = 'DoorView.singleFold';
            this.add(this.singleFold);
            this.highlights.add(this.singleFold.first.frame);
            this.highlights.add(this.singleFold.second.frame);

            // We may have too many handles if the door was previously double
            // or bi-fold-double, so we discard the extra
            if(this.model.handles.length > 1) {
                this.model.handles.length = 1;
            }
            // we could have zero handles, so check that we have a one and only
            if(this.model.handles.length == 1) {
                const view = new HandleView(this.model.handles[0], this.app);
                this.singleFold.second.handle = view;
                this.singleFold.second.panel.add(view);
            }
        }
        else {
            log.debug('DoorView.make', this.uuid);
            this.single = new SingleDoor();
            this.add(this.single);
            this.highlights.add(this.single.frame);
            this.highlights.add(this.single.panel);

            // We may have too many handles if the door was previously double
            // or bi-fold-double, so we discard the extra
            if(this.model.handles.length > 1) {
                this.model.handles.length = 1;
            }
            // we could have zero handles, so check that we have a one and only
            if(this.model.handles.length == 1) {
                const view = new HandleView(this.model.handles[0], this.app);
                this.single.handle = view;
                this.single.panel.add(view);
            }
        }
    }

    bind() {
        log.debug('DoorView.bind', this.uuid);

        this.updateGeometry = this.updateGeometry.bind(this);
        this.layout = this.layout.bind(this);

        const door = this.model;

        door.bind('width', this.updateGeometry);
        door.on('height', this.updateGeometry);
        door.on('frame', this.updateGeometry);
        door.frame?.on('style', this.updateGeometry);
        door.on('panel', this.updateGeometry);
        door.panel.on('style', this.updateGeometry);

        door.on('hinge', () => {
            Application.do('refresh');
        });

        door.on('open', this.layout);

        // Frame/Panel for double doors
        if(door.hinge == 'double') {
            door.bind('frame', () => {
                if(door.frame) {
                    this.bindMaterial(
                        this.double.left.frame, this.model.frame.material);
                    this.bindMaterial(
                        this.double.right.frame, this.model.frame.material);
                } else {
                    this.double.left.frame.visible = false;
                    this.double.right.frame.visible = false;
                }
            });
            door.bind('panel', () => {
                this.bindMaterial(
                    this.double.left.panel, this.model.panel.material);
                this.bindMaterial(
                    this.double.right.panel, this.model.panel.material);
            });
        }
        else if(door.hinge == 'bi-fold-double') {
            door.bind('frame', () => {
                if(door.frame) {
                    this.bindMaterial(
                        this.doubleFold.left.first.frame, this.model.frame.material);
                    this.bindMaterial(
                        this.doubleFold.left.second.frame, this.model.frame.material);
                    this.bindMaterial(
                        this.doubleFold.right.first.frame, this.model.frame.material);
                    this.bindMaterial(
                        this.doubleFold.right.second.frame, this.model.frame.material);
                } else {
                    this.doubleFold.left.first.frame.visible = false;
                    this.doubleFold.left.second.frame.visible = false;
                    this.doubleFold.right.first.frame.visible = false;
                    this.doubleFold.right.second.frame.visible = false;
                }
            });
            door.bind('panel', () => {
                this.bindMaterial(
                    this.doubleFold.left.first.panel, this.model.panel.material);
                this.bindMaterial(
                    this.doubleFold.left.second.panel, this.model.panel.material);
                this.bindMaterial(
                    this.doubleFold.right.first.panel, this.model.panel.material);
                this.bindMaterial(
                    this.doubleFold.right.second.panel, this.model.panel.material);
            });
        }
        else if(door.hinge.startsWith('bi-fold')) {
            door.bind('frame', () => {
                if(door.frame) {
                    this.bindMaterial(
                        this.singleFold.first.frame, this.model.frame.material);
                    this.bindMaterial(
                        this.singleFold.second.frame, this.model.frame.material);
                } else {
                    this.singleFold.first.frame.visible = false;
                    this.singleFold.second.frame.visible = false;
                }
            });
            door.bind('panel', () => {
                this.bindMaterial(
                    this.singleFold.first.panel, this.model.panel.material);
                this.bindMaterial(
                    this.singleFold.second.panel, this.model.panel.material);
            });
        }
        // Frame/Panel for single doors
        else {
            door.bind('frame', () => {
                if(door.frame) {
                    this.bindMaterial(
                        this.single.frame, this.model.frame.material);
                } else {
                    this.single.frame.visible = false;
                }
            });
            door.bind('panel', () => {
                this.bindMaterial(
                    this.single.panel, this.model.panel.material);
            });
        }

        door.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            if(this.single) {
                if(this.single.frame) this.single.frame.castShadow = castShadow;
                this.single.panel.castShadow = castShadow;
            }
            if(this.double) {
                if(this.double.left.frame) {
                    this.double.left.frame.castShadow = castShadow;
                }
                this.double.left.panel.castShadow = castShadow;
                if(this.double.right.frame) {
                    this.double.right.frame.castShadow = castShadow;
                }
                this.double.right.panel.castShadow = castShadow;
            }
            for(const handle of door.handles) {
                handle.opacity = door.opacity;
            }
        });
    }

    updateGeometry() {
        log.debug('DoorView.updateGeometry', this.uuid);
        let el: number;
        if(this.model.parent instanceof Section) {
            el = elevation(this.model.parent);
        } else {
            el = this.model.position.y;
        }
        const properties = {
            frame: this.model.frame,
            panel: this.model.panel,
            width: this.model.width,
            height: this.model.height,
            elevation: el,
        };
        if(this.model.hinge == 'double') {
            this.double.left.updateGeometry({
                ... properties,
                hinge: 'left',
                width: this.model.width / 2,
            });
            this.double.right.updateGeometry({
                ... properties,
                hinge: 'right',
                width: this.model.width / 2,
            });
            this.double.left.position.x = 0;
            this.double.right.position.x = this.model.width / 2;
        }
        else if(this.model.hinge == 'bi-fold-double') {
            this.doubleFold.left.updateGeometry({
                ... properties,
                hinge: 'left',
                width: this.model.width / 2,
            });
            this.doubleFold.right.updateGeometry({
                ... properties,
                hinge: 'right',
                width: this.model.width / 2,
            });
        }
        else if(this.model.hinge.startsWith('bi-fold')) {
            const side = this.model.hinge.replace('bi-fold-', '');
            this.singleFold.updateGeometry(
                {... properties, hinge: side as Side});
        }
        else {
            this.single.updateGeometry(
                {... properties, hinge: this.model.hinge as Side});
        }
        this.layout();
    }

    layout() {
        log.debug('DoorView.layout', this.uuid);
        const open = this.model.open;
        const properties = {
            frame: this.model.frame,
            panel: this.model.panel,
            width: this.model.width,
            height: this.model.height,
        }
        switch(this.model.hinge) {
            case 'left':
                this.single.updatePivot({...properties, hinge: 'left'});
                this.single.pivot.rotation.y = -Math.PI * open;
                break;
            case 'right':
                this.single.updatePivot({...properties, hinge: 'right'});
                this.single.pivot.rotation.y = Math.PI * open;
                break;
            case 'top':
                this.single.updatePivot({...properties, hinge: 'top'});
                this.single.pivot.rotation.x = -Math.PI * open;
                break;
            case 'bottom':
                this.single.updatePivot({...properties, hinge: 'bottom'});
                this.single.pivot.rotation.x = Math.PI * open;
                break;
            case 'double':
                this.double.left.updatePivot({
                    ...properties,
                    hinge: 'left',
                    width: this.model.width/2,
                });
                this.double.right.updatePivot({
                    ...properties,
                    hinge: 'right',
                    width: this.model.width/2,
                });
                this.double.left.pivot.rotation.y = -Math.PI * open;
                this.double.right.pivot.rotation.y = Math.PI * open;
                break;
            case 'bi-fold-left':
                // we don't have to adjust the width here because the pivot is
                // at (0, 0, 0), which will work fine regardless
                this.singleFold.first.updatePivot({...properties, hinge: 'left'});
                this.singleFold.first.pivot.rotation.y = -Math.PI * open;
                this.singleFold.second.updatePivot({...properties, hinge: 'left'});
                this.singleFold.second.position.x = this.model.width / 2;
                this.singleFold.second.pivot.rotation.y =
                    clamp(2 * Math.PI * open, 0, Math.PI);
                break;
            case 'bi-fold-right':
                // Here we need to tell the SingleDoor it's half our width so
                // that it can position the pivot correctly.
                this.singleFold.first.updatePivot({
                    ...properties,
                    width: this.model.width / 2,
                    hinge: 'right'});
                this.singleFold.first.position.x = this.model.width / 2;
                this.singleFold.first.pivot.rotation.y = Math.PI * open;
                this.singleFold.second.updatePivot({
                    ...properties,
                    width: this.model.width / 2,
                    hinge: 'right'});
                this.singleFold.second.position.x = -this.model.width / 2;
                this.singleFold.second.pivot.rotation.y =
                    clamp(2 * -Math.PI * open, -Math.PI, 0);
                break;
            case 'bi-fold-double':
                // Here we combine the procedures for the left and right
                // bi-fold doors, but we additionally need to consider that the
                // total space is divided into _four_ units
                this.doubleFold.left.first.updatePivot({...properties, hinge: 'left'});
                this.doubleFold.left.first.pivot.rotation.y = -Math.PI * open;
                this.doubleFold.left.second.updatePivot({...properties, hinge: 'left'});
                this.doubleFold.left.second.position.x = this.model.width / 4;
                this.doubleFold.left.second.pivot.rotation.y =
                    clamp(2 * Math.PI * open, 0, Math.PI);

                this.doubleFold.right.first.updatePivot({
                    ...properties,
                    width: this.model.width / 4,
                    hinge: 'right'});
                this.doubleFold.right.first.position.x = 3 * this.model.width / 4;
                this.doubleFold.right.first.pivot.rotation.y = Math.PI * open;
                this.doubleFold.right.second.updatePivot({
                    ...properties,
                    width: this.model.width / 4,
                    hinge: 'right'});
                this.doubleFold.right.second.position.x = -this.model.width / 4;
                this.doubleFold.right.second.pivot.rotation.y =
                    clamp(2 * -Math.PI * open, -Math.PI, 0);
                break;
        }
        if(this.single) {
            this.placeHandle(this.single.handle);
        }
        else if(this.double) {
            this.placeHandle(this.double.left.handle);
            this.placeHandle(this.double.right.handle);
        } else if(this.singleFold) {
            this.placeHandle(this.singleFold.first.handle);
            this.placeHandle(this.singleFold.second.handle);
        } else if(this.doubleFold) {
            this.placeHandle(this.doubleFold.left.first.handle);
            this.placeHandle(this.doubleFold.left.second.handle);
            this.placeHandle(this.doubleFold.right.first.handle);
            this.placeHandle(this.doubleFold.right.second.handle);
        }
    }

    placeHandle = (view: HandleView) => {
        if(!view) return;
        if(view.geometryAvailable) {
            box.min.set(0, 0, 0); box.max.set(0, 0, 0);
            getGeometrySize(view.handle, box);
            // handles in the new assets are stored on their side and rotated,
            // but the old ones have models for the different rotations. In any
            // case, we just want the larger of the two dimensions.
            const height = Math.max(
                box.max.x - box.min.x,
                box.max.y - box.min.y
            );

            if(isExtrusionPull(view.model)) {
                // extrusion pull positions never need to be updated
            }
            else if(["top", "bottom"].includes(this.model.hinge)) {
                // no dont move the handle, it's fine already
            }
            else if(this.model.parent instanceof Section)
            {
                const el = elevation(this.model.parent);
                setHandleHeight(el, view.model, height);
            }
            else if(this.model.parent instanceof Wall)
            {
                const el = this.model.position.y;
                setHandleHeight(el, view.model, height);
            }
            else {
                const
                    p = this.model.parent as DomainModel3D,
                    el = p.position.y;
                setHandleHeight(el, view.model, height);
            }
        } else {
            //view.onGeometryAvailable.once(this.placeHandle);
            // Instead of calling ourself, we call ourself indirectly through
            // the updateGeometry method. That will cause the handle placement
            // logic to re-run in full.
            view.onGeometryAvailable.once(this.updateGeometry);
        }
    }
}
